import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import type {
  ChaoMapaCell,
  ChaoMapaCelulaDetalhe,
  ChaoMapaMedicaoTab,
  ChaoPendenciaMotivo,
} from "@/services/chaoDeObra";
import {
  abrirChaoPendencia,
  fetchChaoMapaCelula,
  resolverChaoPendencia,
  saveChaoPrevisao,
  saveChaoServiceProgress,
} from "@/services/chaoDeObra";
import {
  formatWhen,
  PENDENCIA_MOTIVO_LABELS,
  shortServiceName,
  STATUS_CELULA_LABELS,
} from "./chaoAvancoMapaStatus";

const ATALHOS = [0, 25, 50, 75, 100] as const;
const MOTIVOS: ChaoPendenciaMotivo[] = [
  "retrabalho",
  "reprovado_vistoria",
  "bloqueado_predecessor",
  "falta_material",
  "outro",
];

interface Props {
  open: boolean;
  onClose: () => void;
  costCenterId: string;
  unitId: string;
  unitLabel: string;
  serviceId: string;
  serviceName: string;
  cell: ChaoMapaCell;
  medicoes: ChaoMapaMedicaoTab[];
  selectedMedicaoId: string | null;
  canEditPct: boolean;
  canEditPlanejamento: boolean;
  onProgressSaved: (pct: number) => void;
  onChanged: () => void;
}

export function ChaoAvancoMapaDrawer({
  open,
  onClose,
  costCenterId,
  unitId,
  unitLabel,
  serviceId,
  serviceName,
  cell,
  medicoes,
  selectedMedicaoId,
  canEditPct,
  canEditPlanejamento,
  onProgressSaved,
  onChanged,
}: Props) {
  const [pct, setPct] = useState(String(Math.round(cell.percentualReal)));
  const [detalhe, setDetalhe] = useState<ChaoMapaCelulaDetalhe | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [motivo, setMotivo] = useState<ChaoPendenciaMotivo>("retrabalho");
  const [detalhePend, setDetalhePend] = useState("");
  const [previsto, setPrevisto] = useState("");

  useEffect(() => {
    setPct(String(Math.round(cell.percentualReal)));
  }, [cell.percentualReal, unitId, serviceId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void fetchChaoMapaCelula({ costCenterId, unitId, serviceId }).then((d) => {
      if (!cancelled) setDetalhe(d);
    });
    return () => {
      cancelled = true;
    };
  }, [open, costCenterId, unitId, serviceId]);

  if (!open) return null;

  const savePct = async (value: number) => {
    setError(null);
    setSaving(true);
    const snapshot = cell.percentualReal;
    onProgressSaved(value);
    try {
      const res = await saveChaoServiceProgress(
        unitId,
        { [serviceId]: value },
        { medicaoId: selectedMedicaoId ?? undefined },
      );
      if (!res.success) {
        onProgressSaved(snapshot);
        setError(res.error ?? "Não foi possível salvar.");
      } else {
        onChanged();
      }
    } catch {
      onProgressSaved(snapshot);
      setError("Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  const n = Math.min(100, Math.max(0, Number(pct) || 0));

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button className="absolute inset-0 bg-black/30" aria-label="Fechar" onClick={onClose} />
      <aside className="relative w-full max-w-md h-full bg-white shadow-modal flex flex-col overflow-hidden">
        <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-100">
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Casa {unitLabel}</p>
            <h3 className="text-base font-semibold text-slate-800 leading-snug">
              {shortServiceName(serviceName)}
            </h3>
            <p className="text-xs text-slate-500 mt-1">{STATUS_CELULA_LABELS[cell.status]}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
            aria-label="Fechar painel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-5">
          <section>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Percentual atual</p>
            <div className="flex gap-2">
              <Input
                type="number"
                min={0}
                max={100}
                value={pct}
                disabled={!canEditPct || saving}
                onChange={(e) => setPct(e.target.value)}
              />
              <Button disabled={!canEditPct || saving} onClick={() => void savePct(n)}>
                Salvar
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {ATALHOS.map((v) => (
                <Button
                  key={v}
                  size="sm"
                  variant="secondary"
                  disabled={!canEditPct || saving}
                  onClick={() => {
                    setPct(String(v));
                    void savePct(v);
                  }}
                >
                  {v}
                </Button>
              ))}
            </div>
            {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
          </section>

          <section className="text-sm text-slate-600 space-y-1">
            <p>
              Previsto na medição selecionada:{" "}
              <strong>{cell.percentualPrevistoMedicaoSelecionada ?? "—"}%</strong>
            </p>
            <p>
              Previsto até a medição atual:{" "}
              <strong>{cell.percentualPrevistoAteMedicaoAtual ?? "—"}%</strong>
            </p>
            <p>
              Atestado: <strong>{cell.percentualAtestado ?? "—"}%</strong>
            </p>
          </section>

          {canEditPlanejamento && selectedMedicaoId && (
            <section>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                Previsão nesta medição
              </p>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={previsto}
                  onChange={(e) => setPrevisto(e.target.value)}
                  placeholder="100"
                />
                <Button
                  variant="secondary"
                  onClick={() => {
                    void saveChaoPrevisao({
                      servicoId: serviceId,
                      casaId: unitId,
                      medicaoId: selectedMedicaoId,
                      percentualPrevisto: Number(previsto) || 0,
                    }).then(onChanged);
                  }}
                >
                  Aplicar
                </Button>
              </div>
            </section>
          )}

          <section>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Pendências</p>
            <ul className="space-y-1.5 text-sm">
              {(detalhe?.pendencias ?? []).map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2">
                  <span>
                    {PENDENCIA_MOTIVO_LABELS[p.motivo] ?? p.motivo}
                    {p.status === "resolvida" ? " · resolvida" : ""}
                  </span>
                  {p.status === "aberta" && canEditPct && (
                    <Button size="sm" variant="ghost" onClick={() => void resolverChaoPendencia(p.id).then(onChanged)}>
                      Resolver
                    </Button>
                  )}
                </li>
              ))}
              {(detalhe?.pendencias ?? []).length === 0 && (
                <li className="text-slate-400">Nenhuma pendência.</li>
              )}
            </ul>
            {canEditPct && (
              <div className="mt-3 flex flex-col gap-2">
                <select
                  className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value as ChaoPendenciaMotivo)}
                >
                  {MOTIVOS.map((m) => (
                    <option key={m} value={m}>
                      {PENDENCIA_MOTIVO_LABELS[m]}
                    </option>
                  ))}
                </select>
                <Input
                  value={detalhePend}
                  onChange={(e) => setDetalhePend(e.target.value)}
                  placeholder="Detalhe (opcional)"
                />
                <Button
                  variant="secondary"
                  onClick={() => {
                    void abrirChaoPendencia({
                      servicoId: serviceId,
                      casaId: unitId,
                      motivo,
                      detalhe: detalhePend,
                    }).then(onChanged);
                  }}
                >
                  Abrir pendência
                </Button>
              </div>
            )}
          </section>

          <section>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Histórico</p>
            <ul className="space-y-1 text-sm text-slate-600">
              {(detalhe?.historico ?? []).slice(0, 20).map((h) => (
                <li key={h.id}>
                  {h.percentualAnterior}% → {h.percentualNovo}% · {formatWhen(h.createdAt)} por {h.usuarioNome}
                </li>
              ))}
              {(detalhe?.historico ?? []).length === 0 && (
                <li className="text-slate-400">Sem alterações registradas.</li>
              )}
            </ul>
            {medicoes.length > 0 && (
              <p className="text-[11px] text-slate-400 mt-3">
                Previsões:{" "}
                {(detalhe?.previsoes ?? [])
                  .map((p) => `M${p.numero} ${p.percentualPrevisto}%`)
                  .join(" · ") || "—"}
              </p>
            )}
          </section>
        </div>
      </aside>
    </div>
  );
}
